// Prices an advisor offer for a billing cycle and a set of counts. One
// function feeds the tour's ticket, its docked bar, the Done page and the lock
// panel, so every surface quotes the same number for the same choice.
//
// Live catalog first — the same tiered prices the checkout charges. While it
// loads, the tour's verified monthly figures × the cycle's PAID months
// (semi-annual pays five, annual pays ten — the checkout's own
// cycleMonthsFree). A loaded catalog with no SKU for a cycle makes that cycle
// unavailable rather than quietly pricing another one.
// @ts-ignore - lib/billing is plain JS
import { BILLING_CYCLES, BILLING_CYCLE_OPTIONS, CHECKOUT_PRICING, CYCLE_CHIP, CYCLE_SUFFIX, PRODUCTS } from '../../lib/billing/config';
// @ts-ignore - lib/billing is plain JS
import { computeLineAmountCents, priceMeta, resolveCatalogPrice } from '../../lib/billing/priceCatalog';
import { formatCents } from '../account-settings/subscriptions/checkout-v2/format';
import { ADVISOR_MATRIX, AdvisorOffer, AssetSide, OfferLine } from './tourData';

export type BillingCycle = 'monthly' | 'quarterly' | 'semiAnnual' | 'annual';
export type OfferVariant = 'base' | 'alt';

/** What the user sets on the ticket: CFD accounts, and futures accounts beyond the bundle. */
export interface OfferCounts {
  cfd: number;
  futuresExtra: number;
}

export function cycleMonths(cycle: string): number {
  return Number((CHECKOUT_PRICING.cycleMultipliers as Record<string, number>)[cycle]) || 1;
}
export function cyclePaidMonths(cycle: string): number {
  return cycleMonths(cycle) - (Number((CHECKOUT_PRICING.cycleMonthsFree as Record<string, number>)[cycle]) || 0);
}
export const cycleSuffix = (cycle: string): string => (CYCLE_SUFFIX as Record<string, string>)[cycle] || '/mo';

export const MONTHLY = BILLING_CYCLES.monthly as BillingCycle;

/** The checkout's own cycles, in its order, with its savings chips. */
export const CYCLE_OPTIONS: Array<{ id: BillingCycle; label: string; chip: string; months: number }> = (
  BILLING_CYCLE_OPTIONS as Array<{ id: BillingCycle; label: string }>
).map((o) => ({ id: o.id, label: o.label, chip: (CYCLE_CHIP as Record<string, string>)[o.id] || '', months: cycleMonths(o.id) }));

export const isBillingCycle = (v: unknown): v is BillingCycle => CYCLE_OPTIONS.some((o) => o.id === v);
export const cycleLabel = (cycle: string): string => CYCLE_OPTIONS.find((o) => o.id === cycle)?.label ?? 'Monthly';

/** The offer the two pickers resolve to. */
export const currentOffer = (from: AssetSide, to: AssetSide): AdvisorOffer => ADVISOR_MATRIX[`${from}-${to}`];

/** The offer with its chosen variant applied (Premium / Unlimited). */
export const resolveOffer = (offer: AdvisorOffer, variant: OfferVariant): AdvisorOffer => {
  if (variant === 'alt' && offer.alt) {
    return { ...offer, plan: offer.alt.plan, lines: offer.alt.lines, product: offer.alt.product, preset: offer.alt.preset };
  }
  return offer;
};

/** The fewest CFD accounts this offer makes sense with (two for CFD→CFD, one for a mixed route), or 0 if none. */
export const cfdMinimum = (offer: AdvisorOffer): number => offer.lines.find((l) => l.counted === 'cfd')?.qty ?? 0;
/** Whether the offer carries the futures bundle the user can add accounts to. */
export const hasFuturesBundle = (offer: AdvisorOffer): boolean => offer.lines.some((l) => l.counted === 'futures');

/** Counts clamped to what the offer allows. */
export const clampCounts = (offer: AdvisorOffer, counts: Partial<OfferCounts>): OfferCounts => ({
  cfd: Math.max(cfdMinimum(offer), Math.round(counts.cfd ?? 0)),
  futuresExtra: hasFuturesBundle(offer) ? Math.max(0, Math.round(counts.futuresExtra ?? 0)) : 0
});

const PRODUCT_KEY: Record<OfferLine['product'], string> = {
  standard: PRODUCTS.standard,
  premium: PRODUCTS.premium,
  futures: PRODUCTS.futures
};

/** The catalog SKU for a line on a cycle — only when it really is that cycle and variant. */
const skuFor = (catalog: unknown, line: OfferLine, cycle: BillingCycle) => {
  const price = resolveCatalogPrice(catalog as any, PRODUCT_KEY[line.product], cycle, line.variant ? { variant: line.variant } : {});
  const meta = priceMeta(price) as { cycle?: string; variant?: string } | null;
  const ok = price?.id && meta?.cycle === cycle && (meta?.variant ?? 'default') === (line.variant ?? 'default');
  return ok ? price : null;
};

export interface QuotedLine {
  label: string;
  cents: number | null;
}

interface PricedLine extends QuotedLine {
  live: boolean;
  missing: boolean;
}

/**
 * A line priced for a cycle and the counts. The catalog resolver may fall back
 * to the default variant when a variant is not stocked, so the SKU that comes
 * back is checked against what was asked for: Unlimited must never be priced
 * as pay-as-you-go, and no cycle is ever priced as another. A futures bundle
 * yields two lines when extra accounts are added — the bundle and the extras
 * — both from the same graduated tiers the checkout bills.
 */
export function priceLines(catalog: unknown, line: OfferLine, cycle: BillingCycle, counts: OfferCounts): PricedLine[] {
  const paid = cyclePaidMonths(cycle);
  const price = catalog ? skuFor(catalog, line, cycle) : null;
  const missing = Boolean(catalog) && !price;

  if (line.counted === 'cfd') {
    const qty = Math.max(line.qty, counts.cfd);
    const label = `${line.label} × ${qty}`;
    if (price) return [{ label, cents: computeLineAmountCents(price, qty), live: true, missing: false }];
    const cents = missing || line.monthlyCents == null ? null : (line.monthlyCents / line.qty) * qty * paid;
    return [{ label, cents, live: false, missing }];
  }

  if (line.counted === 'futures') {
    const included = (price && Number(price?.tiers?.[0]?.up_to)) || line.qty;
    const extra = counts.futuresExtra;
    const base: PricedLine = price
      ? { label: `${line.label} · ${included} accounts`, cents: computeLineAmountCents(price, included), live: true, missing: false }
      : {
          label: `${line.label} · ${included} accounts`,
          cents: missing || line.monthlyCents == null ? null : line.monthlyCents * paid,
          live: false,
          missing
        };
    if (extra <= 0) return [base];
    const extras: PricedLine = price
      ? {
          label: `Extra futures accounts × ${extra}`,
          cents: computeLineAmountCents(price, included + extra) - computeLineAmountCents(price, included),
          live: true,
          missing: false
        }
      : {
          label: `Extra futures accounts × ${extra}`,
          cents: missing ? null : extra * CHECKOUT_PRICING.futures.extraAccountMonthlyCents * paid,
          live: false,
          missing
        };
    return [base, extras];
  }

  if (price) {
    const qty = line.bundle ? Number(price?.tiers?.[0]?.up_to) || line.qty : line.qty;
    return [{ label: line.label, cents: computeLineAmountCents(price, qty), live: true, missing: false }];
  }
  return [{ label: line.label, cents: missing || line.monthlyCents == null ? null : line.monthlyCents * paid, live: false, missing }];
}

/** Price of one more unit on a cycle — for the stepper's "per account" note. */
export function unitCents(catalog: unknown, offer: AdvisorOffer, cycle: BillingCycle, which: 'cfd' | 'futures'): number | null {
  const line = offer.lines.find((l) => l.counted === which);
  if (!line) return null;
  const price = catalog ? skuFor(catalog, line, cycle) : null;
  const paid = cyclePaidMonths(cycle);
  if (which === 'cfd') {
    if (price) return computeLineAmountCents(price, 1);
    return line.monthlyCents == null ? null : (line.monthlyCents / line.qty) * paid;
  }
  if (price) {
    const included = Number(price?.tiers?.[0]?.up_to) || line.qty;
    return computeLineAmountCents(price, included + 1) - computeLineAmountCents(price, included);
  }
  return CHECKOUT_PRICING.futures.extraAccountMonthlyCents * paid;
}

export interface OfferQuote {
  cycle: BillingCycle;
  counts: OfferCounts;
  /** The plan, named for the counts: "CFD copier · 3 accounts". */
  title: string;
  lines: QuotedLine[];
  /** Null while a line is still unpriced. */
  totalCents: number | null;
  /** '/mo', '/qtr', '/6mo', '/yr' — the checkout's own suffixes. */
  suffix: string;
  months: number;
  /** 'Save 2 months' and friends, or ''. */
  chip: string;
  /** The total spread over the months it covers; null on monthly. */
  perMonthCents: number | null;
  /** Futures accounts in the bundle (live tier boundary), or 0 without a bundle. */
  futuresIncluded: number;
  /** Every line priced from the live catalog. */
  live: boolean;
  /** Some line has no SKU on this cycle — it cannot be bought this way. */
  unavailable: boolean;
}

const DEFAULT_COUNTS: OfferCounts = { cfd: 0, futuresExtra: 0 };

export function quoteOffer(
  catalog: unknown,
  offer: AdvisorOffer,
  cycle: BillingCycle,
  counts: Partial<OfferCounts> = DEFAULT_COUNTS
): OfferQuote {
  const c = clampCounts(offer, counts);
  const priced = offer.lines.flatMap((l) => priceLines(catalog, l, cycle, c));
  const totalCents = priced.every((l) => l.cents != null) ? priced.reduce((n, l) => n + (l.cents as number), 0) : null;
  const months = cycleMonths(cycle);
  const bundle = offer.lines.find((l) => l.counted === 'futures');
  const bundlePrice = bundle && catalog ? skuFor(catalog, bundle, cycle) : null;
  const futuresIncluded = bundle ? Number(bundlePrice?.tiers?.[0]?.up_to) || bundle.qty : 0;
  const parts: string[] = [];
  if (cfdMinimum(offer) > 0) parts.push(`${c.cfd} ${offer.logos === 'both' ? 'CFD ' : ''}account${c.cfd === 1 ? '' : 's'}`);
  if (bundle) parts.push(`${futuresIncluded + c.futuresExtra} futures accounts`);
  const title =
    offer.logos === 'futures' && bundle
      ? `${offer.plan} · ${futuresIncluded + c.futuresExtra} accounts`
      : parts.length
      ? `${offer.plan} · ${parts.join(' + ')}`
      : offer.plan;
  return {
    cycle,
    counts: c,
    title,
    lines: priced.map(({ label, cents }) => ({ label, cents })),
    totalCents,
    suffix: cycleSuffix(cycle),
    months,
    chip: CYCLE_OPTIONS.find((o) => o.id === cycle)?.chip ?? '',
    perMonthCents: totalCents == null || months === 1 ? null : Math.round(totalCents / months),
    futuresIncluded,
    live: priced.every((l) => l.live),
    unavailable: priced.some((l) => l.missing)
  };
}

/** Cycles this offer can actually be bought on — every cycle while the catalog loads. */
export function availableCycles(catalog: unknown, offer: AdvisorOffer): BillingCycle[] {
  return CYCLE_OPTIONS.map((o) => o.id).filter((c) => !catalog || !quoteOffer(catalog, offer, c).unavailable);
}

/** The chosen cycle when the offer is sold on it, else monthly. */
export function effectiveCycle(catalog: unknown, offer: AdvisorOffer, cycle: BillingCycle): BillingCycle {
  return availableCycles(catalog, offer).includes(cycle) ? cycle : MONTHLY;
}

/** '$200/yr', or '—' while unpriced. */
export const formatQuote = (q: OfferQuote): string => (q.totalCents == null ? '—' : `${formatCents(q.totalCents)}${q.suffix}`);
